import React, { useContext, useEffect, useRef, useState } from "react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { useParams } from "react-router-dom";
import CustomDialog, { CustomDialogProps } from "../../../../components/Dialog/CustomDialog";
import { TestSeriesContext, TestSeriesTabType } from "../../../../contexts/TestSeriesContext";
import TestSeriesConfiguratorTab from "./TestConfiguratorTab/TestSeriesConfiguratorTab";
import TestCasesTab from "./TestCasesTab/TestCasesTab";
import { tesaDarkRedError } from "../../../../styles/colors";
import ConfirmationDialogContext from "../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { TestSeriesTableRef } from "./TestConfiguratorTab/TestSeriesTable";
import TestSeriesGallery from "./TestSeriesGallery";
import TestSeriesVersionMenu from "./TestSeriesVersionMenu";
import RequestFlagIconList from "../../../../components/RequestFlags/RequestFlagIconList";
import DeepLink from "../../../../components/DeepLink";
import { TestRequestTabType } from "../../useTestRequestParams";
import useEnv from "../../../../hooks/env";
import HistoricalTestCasesApi from "../../../../api/services/HistoricalTestCasesApi";
import { getProductSampleIds, getSubstrateSampleIds } from "../utils/SampleMinimalReferences";
import ErrorSnackbarContext from "../../../../contexts/ErrorSnackbarContext";
import TestSeriesDialogContext, { initialFilterOptions } from "./TestSeriesTabbedDialogContext";
import { TestCasesOverviewTableRef } from "./TestCasesTab/TestCasesOverviewTable";
import useStyles from "./TestSeriesTabbedDialogStyle";
import HistoricalTestCaseSearchFilterRQ from "../../../../api/services/HistoricalTestCaseSearchFilterRQ";
import TestSeriesApi from "../../../../api/services/TestSeriesApi";
import { isEqual, isEqualArrayObjects } from "../utils/DeepEqual";
import TestPlanPermissionContext from "../../../../contexts/TestPlanPermissionContext";
import { AnalyzeHistoricalFormType } from "../AnalyzeHistoricalForm";
import createTmbActionIdObject from "../utils/CollectTmbIds";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box paddingY={3}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `create-request-${index}`,
    "aria-controls": `create-request-panel-${index}`,
  };
}

interface TestSeriesTabbedDialogProps extends CustomDialogProps {
  loadDataInParent: () => void;
  onSevereError: () => void;
  onTabChange: (tab: TestSeriesTabType) => void;
  historicalSearchSettings: AnalyzeHistoricalFormType | null;
}

const TestSeriesTabbedDialog: React.FC<TestSeriesTabbedDialogProps> = (props) => {
  const {
    onTabChange: handleTabChange,
    onClose,
    open,
    loadDataInParent,
    onSevereError,
    historicalSearchSettings,
  } = props;
  const {
    selectedTestSeriesObject,
    selectedTestSeriesHasTestCases,
    permissions,
    openTabValue,
    originRequestId,
    updateOpenTabValue,
    testSeriesCurrentVersion,
    updateTestSeriesCurrentVersion,
    testSeriesIsReused,
  } = useContext(TestSeriesContext);

  const { showSnackbar } = useContext(ErrorSnackbarContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { analyzeHistoricalData } = useContext(TestPlanPermissionContext);

  const [showExcludedTests, setShowExcludedTests] = useState(selectedTestSeriesObject.testSeriesStatus === "DRAFT");
  const [testSeriesVersions, setTestSeriesVersions] = useState(selectedTestSeriesObject.versions);
  const [loadingHistoricalTestCases, setLoadingHistoricalTestCases] = useState(true);
  const [historicalTestCasesMatchCounter, setHistoricalTestCasesMatchCounter] = useState(0);
  const [filterOptionsForHistoricalTestCases, setFilterOptionsForHistoricalTestCases] =
    useState<typeof initialFilterOptions>(initialFilterOptions);

  const { api } = useEnv();
  const testSeriesApi = new TestSeriesApi(api);
  const historicalTestCasesApi = new HistoricalTestCasesApi(api);

  const { testRequestId } = useParams<{ testRequestId: string }>();

  const classes = useStyles();

  const lastApiCallParamsRef = useRef({
    productSampleIds: [] as Array<number>,
    substrateSampleIds: [] as Array<number>,
    testMethodReferences: [] as Array<{ id: number; version: number }>,
  });

  let testSeriesTable: TestSeriesTableRef | null = null;
  let testCasesOverviewTable: TestCasesOverviewTableRef | null = null;

  const handleChangeTestSeries = (testSeriesTableRef: TestSeriesTableRef, changeTab: () => void) => {
    if (testSeriesTableRef.isNew()) {
      testSeriesTableRef
        ?.saveAndGenerate()
        .then(() => {
          changeTab();
        })
        .catch(() => {});
    } else if (testSeriesTableRef.isDirty()) {
      showConfirmationDialog({
        headline: "You have unsaved changes",
        text: "If you continue, your current changes to the test configurator will be discarded.",
        confirmText: "Save",
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      })
        .then((id) => {
          if (id === "DEFAULT") {
            return testSeriesTableRef.saveAndGenerate().then(() => {
              changeTab();
            });
          }
          changeTab();
        })
        .catch(() => {});
    } else {
      changeTab();
    }
  };

  const handleChangeTestCasesOverviewTable = (
    testCasesOverviewTableRef: TestCasesOverviewTableRef,
    changeTab: () => void,
  ) => {
    const dirty = testCasesOverviewTableRef.isDirty();
    if (dirty) {
      testCasesOverviewTableRef.save().then(changeTab);
    } else {
      changeTab();
    }
  };

  const handleChange = (event: React.ChangeEvent<unknown>, newValue: TestSeriesTabType) => {
    if (testSeriesTable && newValue !== TestSeriesTabType.TestSeriesConfigurator) {
      handleChangeTestSeries(testSeriesTable, () => handleTabChange(newValue));
    } else if (testCasesOverviewTable && newValue !== TestSeriesTabType.TestTable) {
      handleChangeTestCasesOverviewTable(testCasesOverviewTable, () => handleTabChange(newValue));
    } else {
      handleTabChange(newValue);
    }
  };

  const handleVersionChanged = (newVersion: number) => {
    updateTestSeriesCurrentVersion(newVersion);
  };

  const onInternalClose = () => {
    if (!testSeriesTable) {
      onClose();
      updateOpenTabValue(TestSeriesTabType.TestSeriesConfigurator);
      return;
    }
    if (testSeriesTable.isDirty()) {
      showConfirmationDialog({
        headline: "You have unsaved changes",
        text: "If you continue, your current changes to the test configurator will be discarded.",
        confirmText: "Save",
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      })
        .then((id) => {
          if (id === "DEFAULT") {
            return testSeriesTable?.save().then(onClose);
          }
          onClose();
        })
        .catch(() => {});
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (open) {
      setTestSeriesVersions(selectedTestSeriesObject.versions);
      setShowExcludedTests(selectedTestSeriesObject.testSeriesStatus === "DRAFT");

      const { testMethodReference } = selectedTestSeriesObject;

      const hasTestMethodReference =
        testMethodReference &&
        Object.keys(testMethodReference).length > 0 &&
        testMethodReference.testMethodId &&
        testMethodReference.version;

      if (hasTestMethodReference) {
        testSeriesApi.fetchTestSeriesById(selectedTestSeriesObject.id).then(({ data }) => {
          const tmbActionIdObj = createTmbActionIdObject(selectedTestSeriesObject);

          const productSampleIds = getProductSampleIds(data.productSection?.productActionFlows || []);
          const firstSubstrateIds = getSubstrateSampleIds(data.substrateSections[0]?.substrateActionFlows || []);
          const secondSubstrateIds = getSubstrateSampleIds(data.substrateSections[1]?.substrateActionFlows || []);

          const uniqueSubstrateSampleIds = Array.from(new Set([...firstSubstrateIds, ...secondSubstrateIds]));

          const currentParams = {
            productSampleIds,
            substrateSampleIds: uniqueSubstrateSampleIds,
            testMethodReferences: [{ id: testMethodReference.testMethodId!, version: testMethodReference.version! }],
          };

          const areParamsDifferent =
            !isEqual(lastApiCallParamsRef.current.productSampleIds, currentParams.productSampleIds) ||
            !isEqual(lastApiCallParamsRef.current.substrateSampleIds, currentParams.substrateSampleIds) ||
            !isEqualArrayObjects(lastApiCallParamsRef.current.testMethodReferences, currentParams.testMethodReferences);

          if (areParamsDifferent) {
            const options: HistoricalTestCaseSearchFilterRQ = {
              ...currentParams,
              productActionTmbIds: tmbActionIdObj.productActionTmbIds,
              substrateActionTmbIds: tmbActionIdObj.substrateActionTmbIds,
              buildingActionTmbIds: tmbActionIdObj.buildingActionTmbIds,
              treatmentActionTmbIds: tmbActionIdObj.treatmentActionTmbIds,
            };

            const paginationOptions = {
              pageIndex: 0,
              pageSize: 100,
            };

            if (analyzeHistoricalData) {
              showSnackbar("Fetching for historical testcases in the background.");
              return (
                historicalTestCasesApi
                  .searchHistoricalTestCases(testRequestId, {
                    filter: options,
                    pagination: paginationOptions,
                  })
                  // eslint-disable-next-line no-shadow
                  .then(({ data }) => {
                    setLoadingHistoricalTestCases(false);
                    setHistoricalTestCasesMatchCounter(data.totalData || 0);
                    setFilterOptionsForHistoricalTestCases(options);
                    lastApiCallParamsRef.current = currentParams;
                  })
                  .catch(() => {
                    setLoadingHistoricalTestCases(false);
                    setHistoricalTestCasesMatchCounter(0);
                  })
              );
            }
            setLoadingHistoricalTestCases(false);
          }
        });
      }
    }
  }, [open, selectedTestSeriesObject, analyzeHistoricalData]);

  return (
    <TestSeriesDialogContext.Provider
      value={{
        showExcludedTests,
        setShowExcludedTests,
        filterOptionsForHistoricalTestCases,
        setFilterOptionsForHistoricalTestCases,
        loadingHistoricalTestCases,
        setLoadingHistoricalTestCases,
        historicalTestCasesMatchCounter,
        setHistoricalTestCasesMatchCounter,
      }}
    >
      <CustomDialog onClose={onInternalClose} open={open} fullScreen>
        <Box className={classes.header}>
          {testSeriesIsReused && (
            <Box style={{ float: "left", marginTop: 4, marginRight: 8 }}>
              <RequestFlagIconList flags={["HISTORICAL"]} />
            </Box>
          )}
          <h2>
            {selectedTestSeriesObject?.name}
            <TestSeriesVersionMenu
              currentVersion={testSeriesCurrentVersion}
              versions={testSeriesVersions}
              onSuccess={handleVersionChanged}
            />
          </h2>
          {testSeriesIsReused && (
            <>
              <Box style={{ display: "inline-block", marginLeft: 8 }}>Reused from</Box>
              <DeepLink
                label={`${originRequestId}-${selectedTestSeriesObject.indexNumber}`}
                pathParams={{ requestId: originRequestId, tab: TestRequestTabType.TEST }}
                searchParams={{
                  testSeriesId: String(selectedTestSeriesObject.id),
                  tab: String(TestSeriesTabType.TestTable),
                  reused: "0",
                }}
                LinkProps={{ rel: "noopener noreferrer", target: "_blank" }}
              />
            </>
          )}
        </Box>
        <Tabs value={openTabValue} variant="fullWidth" onChange={handleChange} aria-label="test series">
          <Tab disabled={!permissions.accessTestConfigurator} label="Test Configurator" {...a11yProps(0)} />
          <Tab
            disabled={!permissions.accessTestcases || (!selectedTestSeriesHasTestCases && !permissions.edit)}
            label="Test Table"
            {...a11yProps(1)}
          />
          <Tab
            disabled={
              !permissions.accessAttachments ||
              !permissions.accessTestcases ||
              (!selectedTestSeriesHasTestCases && !permissions.edit)
            }
            label="PICTURES & DOCUMENTS"
            {...a11yProps(2)}
          />
        </Tabs>
        <TabPanel value={openTabValue} index={0}>
          <TestSeriesConfiguratorTab
            testSeriesRef={(ref) => {
              if (ref) {
                testSeriesTable = ref;
              }
            }}
            loadDataInParent={loadDataInParent}
            onSevereError={onSevereError}
            testSeriesVersion={testSeriesCurrentVersion}
            historicalSearchSettings={historicalSearchSettings}
          />
        </TabPanel>
        <TabPanel value={openTabValue} index={1}>
          <TestCasesTab
            testCasesOverviewTableRef={(ref) => {
              if (ref) {
                testCasesOverviewTable = ref;
              }
            }}
            onClose={() => {
              onClose?.();
              updateOpenTabValue(TestSeriesTabType.TestSeriesConfigurator);
            }}
            refreshOverviewTable={loadDataInParent}
            testSeriesVersion={testSeriesCurrentVersion}
            historicalSearchSettings={historicalSearchSettings}
          />
        </TabPanel>
        <TabPanel value={openTabValue} index={2}>
          <TestSeriesGallery
            disabled={!permissions.editTestCaseAttachmentsAndNotes && !permissions.editResultAttachmentsAndNotes}
          />
        </TabPanel>
      </CustomDialog>
    </TestSeriesDialogContext.Provider>
  );
};

export default TestSeriesTabbedDialog;
